import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { decode } from 'jsonwebtoken'

import { store } from 'base/store'

import { AuthenticationService } from './AuthenticationService'

/**
 * Existing API endpoints
 */
export const ApiUrls = {
  authentication: {
    jwt: '/api/v2/auth/jwt'
  },
  pull: {
    base: '/api/v2/pull',
    all: '/api/v2/pull/all',
    ucrs: '/api/v2/pull/ucrs'
  },
  messages: {
    base: '/api/v2/messages'
  },
  messageChannel: {
    base: '/api/v2/message-channels',
    messages: '/api/v2/message-channels/messages',
    activity: '/api/v2/message-channels/activity'
  },
  dashboard: {
    base: '/api/v2/dashboards'
  },
  news: {
    confirm: '/api/v2/news/confirm'
  },
  file: {
    open: '/api/v2/file/open'
  },
  events: {
    confirm: '/api/v2/events/confirm'
  },
  alarms: {
    confirm: '/api/v2/alarms/confirm'
  },
  usingVehicles: {
    getStatusLog: '/api/v2/using-vehicles/get-status-log'
  }
}

/**
 * Service to make authenticated API Requests, automatically updates json web token
 */
export class ApiRequestService {

  private static get config(): AxiosRequestConfig | undefined {
    if (!AuthenticationService.jsonWebToken) return

    return {
      headers: {
        'Authorization': 'Bearer ' + AuthenticationService.jsonWebToken
      }
    }
  }

  static async get(url: string) {
    await this.ensureValidAuthenticated()
    return axios.get(url, this.config)
  }

  static async post(url: string, data?: any) {
    await this.ensureValidAuthenticated()
    return axios.post(url, data, this.config)
  }

  static async put(url: string, data?: any) {
    await this.ensureValidAuthenticated()
    return axios.put(url, data, this.config)
  }

  static async delete(url: string) {
    await this.ensureValidAuthenticated()
    return axios.delete(url, this.config)
  }

  private static async ensureValidAuthenticated() {
    if (!AuthenticationService.jsonWebToken) {
      if (!AuthenticationService.accesskey) throw new Error('No valid accesskey nor json web token available')

      await this.updateJWT()
      return
    }

    // Update if jwt is expired
    try {
      const jsonWebToken: any = decode(AuthenticationService.jsonWebToken)
      if (!jsonWebToken) {
        await this.updateJWT()
        return
      }

      if (!jsonWebToken.exp || jsonWebToken.exp * 1000 < Date.now()) {
        await this.updateJWT()
        return
      }
    } catch (error) {
      await this.updateJWT()
      return
    }
  }

  private static async updateJWT() {
    await axios.get(ApiUrls.authentication.jwt + '?accesskey=' + AuthenticationService.accesskey)
      .then((response: AxiosResponse) => {
        void store.dispatch('setJWT', response.data.data.jwt)
      })
      .catch((error: AxiosError) => {
        console.error('Failed to retrieve JSON web token', error)
      })
  }
}
